import { useTranslation } from "react-i18next";
import { ChevronLeft, Sparkles } from "lucide-react";
import { Button } from "components/common/Button";
import { InlineSwitcher } from "components/common/InlineSwitcher";
import { SidebarSortControl } from "../../SidebarSortControl";
import { ONBOARDING_ANCHORS } from "../../Onboarding/onboardingSelectors";
import type { PlanFilterMode, SidebarMode } from "../../utils/types";
import type { SidebarHeaderProps } from "./types";

export const SidebarHeader = ({
  sidebarMode,
  hasDiagrams,
  onSidebarModeChange,
  onCollapse,
  sortMode,
  onSortModeChange,
  planFilter,
  onPlanFilterChange,
  onSuggestFolders,
}: SidebarHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="px-4 py-3 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <div data-onboarding={ONBOARDING_ANCHORS.sidebarTabs}>
          <InlineSwitcher<SidebarMode>
            value={sidebarMode}
            onChange={onSidebarModeChange}
            listClassName="text-sm"
            options={[
              {
                value: "plans",
                label: t("smartSurveyor.common.plans"),
              },
              ...(hasDiagrams
                ? [
                    {
                      value: "diagrams" as const,
                      label: t("smartSurveyor.common.diagrams"),
                    },
                  ]
                : []),
            ]}
          />
        </div>
        <button
          type="button"
          onClick={onCollapse}
          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
        >
          <ChevronLeft size={16} />
        </button>
      </div>
      <div className="mt-2">
        <SidebarSortControl value={sortMode} onChange={onSortModeChange} />
      </div>
      {sidebarMode === "plans" && (
        <div className="mt-2">
          <InlineSwitcher<PlanFilterMode>
            value={planFilter}
            onChange={onPlanFilterChange}
            variant="segmented"
            listClassName="text-sm"
            options={[
              {
                value: "active",
                label: t("smartSurveyor.planFilter.active"),
              },
              {
                value: "archived",
                label: t("smartSurveyor.planFilter.archived"),
              },
            ]}
          />
        </div>
      )}
      {onSuggestFolders !== undefined && (
        <Button
          variant="primary"
          size="sm"
          onClick={onSuggestFolders}
          startAdornment={<Sparkles size={14} />}
          className="mt-2 w-full gap-1.5 rounded-full"
        >
          {t("smartSurveyor.folders.suggestion.button")}
        </Button>
      )}
    </div>
  );
};
